import { Injectable, OnDestroy, effect, untracked } from '@angular/core';
import { BaseServiceResponse } from '../../models/api-calls/base-service-response';
import { NotificationPreferenceResponse } from '../../models/dtos/notification/notification-dtos';
import { SessionService } from '../account-service/session.service';
import { ApiWrapperService } from '../api-wrapper-service/api-wrapper-services';
import { resolveNotificationPreference, sameId } from './notification-preference-policy';

// Channel IDs follow the backend's NotificationChannel enumeration.
const onScreenChannel = 3;

// This is an account-scoped, verified snapshot for automatic growls, not the preferences form's draft.
// No automatic growl is emitted until an authorised preferences read has completed successfully.
@Injectable({ providedIn: 'root' })
export class NotificationGrowlPreferencesService implements OnDestroy {
  // Private variables

  private identity = '';
  private requestVersion = 0;
  private snapshot?: NotificationPreferenceResponse;
  private destroyed = false;

  // Constructors

  constructor(private readonly api: ApiWrapperService, private readonly session: SessionService) {
    effect(() => {
      const identity = this.sessionIdentity();
      untracked(() => this.switchIdentity(identity));
    });
  }

  // Public methods

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.requestVersion++;
    this.snapshot = undefined;
  }

  public allows(notificationType: string, clubId: string | null): boolean {
    const identity = this.currentIdentity();
    const settings = this.snapshot;

    if (!identity || !settings)
      return false;

    // The response lists clubs for which this account can configure notification preferences.
    // Do not display club event content or links for an unrecognised or no-longer-visible club.
    if (clubId && !settings.clubs.some(club => sameId(club.id, clubId)))
      return false;

    const type = settings.types.find(item => sameId(item.id, notificationType));

    if (!type || !type.channels.includes(onScreenChannel))
      return false;

    return resolveNotificationPreference(settings.overrides, clubId, type.id, onScreenChannel);
  }

  public refresh(): void {
    if (!this.currentIdentity())
      return;

    this.snapshot = undefined;
    void this.reload();
  }

  // The preferences page is itself an authorised saved-state reader. Reuse its confirmed result
  // immediately, so a change to the on-screen channel takes effect without navigating away.
  public acceptConfirmedSnapshot(settings: NotificationPreferenceResponse, identity: string): void {
    if (!this.currentIdentity() || identity !== this.identity || !this.isValidSnapshot(settings))
      return;

    this.requestVersion++;
    this.snapshot = settings;
  }

  // An uncertain preference write may have changed the server policy. Fail closed until a
  // subsequent successful read rather than continuing to display growls using stale choices.
  public invalidateUncertainSnapshot(identity: string): void {
    if (this.currentIdentity() !== identity)
      return;

    this.requestVersion++;
    this.snapshot = undefined;
  }

  // Private methods

  private async reload(): Promise<void> {
    const identity = this.identity;
    const version = ++this.requestVersion;

    try
    {
      const response = BaseServiceResponse.fromServiceResponse(await this.api.getNotificationPreferences());

      if (!this.isCurrent(identity, version))
        return;

      if (response.succeeded && response.payload && this.isValidSnapshot(response.payload))
        this.snapshot = response.payload;
    }
    catch
    {
      // Unknown preferences fail closed. No guessed default for automatic UI delivery.
    }
  }

  private switchIdentity(identity: string): void {
    if (identity === this.identity)
      return;

    this.identity = identity;
    this.requestVersion++;
    this.snapshot = undefined;

    if (identity)
      void this.reload();
  }

  private currentIdentity(): string {
    if (this.destroyed)
      return '';

    const identity = this.sessionIdentity();
    this.switchIdentity(identity);
    return identity;
  }

  private sessionIdentity(): string {
    const account = this.session.account();

    return this.session.status() === 'authenticated' && account?.id
      ? `${account.id}:${this.session.version}` : '';
  }

  private isCurrent(identity: string, version: number): boolean {
    return !this.destroyed && !!identity && identity === this.currentIdentity() && version === this.requestVersion;
  }

  private isValidSnapshot(value: NotificationPreferenceResponse): boolean {
    return !!value && Array.isArray(value.overrides) && Array.isArray(value.clubs) && Array.isArray(value.types) &&
      value.overrides.every(item => !!item && typeof item.channel === 'number' && typeof item.enabled === 'boolean' &&
        (item.clubId == null || typeof item.clubId === 'string') &&
        (item.notificationType == null || typeof item.notificationType === 'string')) &&
      value.clubs.every(club => !!club && typeof club.id === 'string') &&
      value.types.every(type => !!type && typeof type.id === 'string' && Array.isArray(type.channels));
  }
}
